import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "@/components/feature/Sidebar";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/base/Pagination";
import { categoryMap } from "@/mocks/categories";
import {
  PAGE_SIZE,
  filterArticles,
  sortArticles,
  uniqueValues,
  type ArticleFilters,
} from "@/lib/content";

interface FilterConfig {
  key: keyof ArticleFilters;
  label: string;
  options: string[];
}

export default function CategoryPage() {
  const { type = "" } = useParams();
  const category = categoryMap[type];

  const [filters, setFilters] = useState<ArticleFilters>({});
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setFilters({});
    setSort("newest");
    setPage(1);
  }, [type]);

  const filterConfigs: FilterConfig[] = useMemo(() => {
    if (type === "laws") {
      return [
        { key: "region", label: "适用地区", options: uniqueValues("laws", "region") },
        { key: "year", label: "颁布年份", options: uniqueValues("laws", "year") },
      ];
    }
    if (type === "ethics") {
      return [{ key: "tag", label: "话题标签", options: uniqueValues("ethics", "tag") }];
    }
    if (type === "cases") {
      return [
        { key: "caseType", label: "案件类型", options: uniqueValues("cases", "caseType") },
        { key: "year", label: "判决年份", options: uniqueValues("cases", "year") },
      ];
    }
    return [];
  }, [type]);

  const results = useMemo(() => {
    if (!category) return [];
    return sortArticles(filterArticles(type, filters), sort);
  }, [category, type, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCount = Object.values(filters).filter(Boolean).length;

  const setFilter = (key: keyof ArticleFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));
  };

  const resetAll = () => {
    setFilters({});
    setSort("newest");
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          你访问的分类不存在或已调整。请从下方选择有效的分类继续浏览。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {Object.values(categoryMap).map((item) => (
            <Link
              key={item.type}
              to={`/category/${item.type}`}
              className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              {item.name}
            </Link>
          ))}
          <Link
            to="/"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const renderFilters = (inPanel = false) => (
    <div className={inPanel ? "flex flex-col gap-6" : "flex flex-wrap items-start gap-x-8 gap-y-5"}>
      {filterConfigs.map((config) => (
        <div key={String(config.key)} className={inPanel ? "" : "min-w-[240px]"}>
          <span className="mb-2 block text-xs font-medium text-foreground-500">{config.label}</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFilter(config.key, "")}
              className={[
                "rounded-full border px-3 py-1 text-xs transition-colors",
                !filters[config.key]
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-background-300 text-foreground-600 hover:bg-background-100",
              ].join(" ")}
            >
              全部
            </button>
            {config.options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setFilter(config.key, option)}
                className={[
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  filters[config.key] === option
                    ? "border-primary-500 bg-primary-50 text-primary-700"
                    : "border-background-300 text-foreground-600 hover:bg-background-100",
                ].join(" ")}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className={inPanel ? "" : "min-w-[200px]"}>
        <span className="mb-2 block text-xs font-medium text-foreground-500">排序</span>
        <div className="flex rounded-full border border-background-300 p-1">
          {[
            { key: "newest", label: "最新发布" },
            { key: "oldest", label: "最早发布" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setSort(item.key)}
              className={[
                "whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors",
                sort === item.key
                  ? "bg-primary-600 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-4 py-8 md:px-8 lg:flex-row">
      <Sidebar activeType={type} />

      <div className="min-w-0 flex-1">
        <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        <header className="mb-6 rounded-lg border border-background-200 bg-background-50 p-6">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-foreground-950">{category.name}分类</h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
                {category.description}
              </p>
            </div>
          </div>
        </header>

        <div className="mb-5 flex items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-primary-700">{results.length}</span> 条内容
            {activeCount > 0 && <span className="ml-2 text-xs text-foreground-500">已应用 {activeCount} 个筛选条件</span>}
          </p>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-accent-700 hover:text-accent-800"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            )}
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选与排序
            </button>
          </div>
        </div>

        <div className="mb-6 hidden rounded-lg border border-background-200 bg-background-50 p-5 lg:block">
          {renderFilters(false)}
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {pageItems.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-search-line text-3xl" />
            </span>
            <h2 className="text-lg font-bold text-foreground-950">没有符合筛选条件的内容</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              当前筛选条件下暂无相关内容。你可以调整或重置筛选条件后重新浏览。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}
      </div>

      {/* 移动端筛选全屏面板 */}
      {panelOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 lg:hidden">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
            <h2 className="text-base font-bold text-foreground-950">筛选与排序</h2>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700"
              aria-label="关闭筛选"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">{renderFilters(true)}</div>
          <div className="flex gap-3 border-t border-background-200 p-4">
            <button
              type="button"
              onClick={() => {
                resetAll();
                setPanelOpen(false);
              }}
              className="flex-1 rounded-md border border-background-300 py-3 text-sm text-foreground-700"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="flex-1 rounded-md bg-primary-600 py-3 text-sm font-medium text-background-50"
            >
              查看 {results.length} 条结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}